import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import * as mapboxgl from "mapbox-gl";
import { MapBoxService } from "../../_services/mapbox.service";
import { ListsSimpleChange } from "../../_components/planning-assistant/classes/util-classes/lists-simple-change";
import { PAUtil } from "../../_components/planning-assistant/classes/util";
import { TourAnimation } from "../../_components/planning-assistant/classes/tour-animation";
import { MapDisplayUnit } from "../../_models/map-display-unit.model";

@Component({
  selector: 'hawk-dashboard-map',
  imports: [],
  templateUrl: './dashboard-map.component.html',
  standalone: true,
  styleUrl: './dashboard-map.component.scss'
})
export class DashboardMapComponent implements AfterViewInit, OnChanges {

  @Input() mapDisplayUnits: MapDisplayUnit<any[], any[]>[] = []
  @Input() navigation: {center?: {lat: number, lng: number}, zoom?: number} = {center: { lat: 51.4, lng: 10.4476 }, zoom: 5}
  @Input() selectedTabIndex: number = 0;

  @ViewChild('MapSizeObsContainer') mapSizeObsContainer: ElementRef<HTMLDivElement>;
  private map: mapboxgl.Map;
  private runningChange: boolean = false;
  private changesQueue: SimpleChanges[] = []
  private initialCenteringDone: boolean = false;

  constructor(
    private mapBoxService: MapBoxService,
  ) { }

  ngAfterViewInit(): void {
    this.waitForMapWindow(200).then(
      _ => {
        this.map = new mapboxgl.Map({
          accessToken: this.mapBoxService.accessToken,
          container: 'map_window',
          style: 'mapbox://styles/mapbox/streets-v9?optimize=true',
          center: this.navigation?.center || { lat: 51.4, lng: 10.4476 },
          zoom: this.navigation?.zoom || 5
        })
      }
    )
    const map_size_observer = new ResizeObserver(entries => {
      entries.forEach(entry => {
        if (this.map) {
          this.map.resize()

          // Beim ersten Resize nach Initialisierung auf Deutschland zentrieren
          // (Container animiert von 0 auf 80%, daher ist initiales Zentrieren fehlerhaft)
          // Delay von 450ms damit die CSS-Transition (400ms) abgeschlossen ist
          if (!this.initialCenteringDone && entry.contentRect.width > 0) {
            this.initialCenteringDone = true;
            setTimeout(() => {
              const center = this.navigation?.center || { lat: 51.4, lng: 10.4476 };
              const zoom = this.navigation?.zoom || 5;
              this.map.setCenter(center);
              this.map.setZoom(zoom);
            }, 450);
          }
        }
      });
    });
    map_size_observer.observe(this.mapSizeObsContainer.nativeElement);
  }

  ngOnChanges(changes: SimpleChanges) {
    // Bei Tab-Wechsel den Zentrierung-Flag zurücksetzen, damit die Karte neu zentriert wird
    if (changes.selectedTabIndex) {
      this.initialCenteringDone = false;
    }

    this.changesQueue.push(this.cloneChanges(changes))
    this.waitForMapAndProcessChanges()
  }

  async processChanges(changes: SimpleChanges): Promise<void> {
    if (changes.mapDisplayUnits) {
      const display_units_changed = new ListsSimpleChange<MapDisplayUnit<any[], any[]>>(changes.mapDisplayUnits);
      display_units_changed.getRemovedValues().map(mdu => mdu.removeFromMap())
      display_units_changed.getAddedValues().map(mdu => mdu.displayOnMap(this.map))
    }

    if (changes.tourAnimations) {
      const tour_animations_changed = new ListsSimpleChange<TourAnimation>(changes.tourAnimations);
      tour_animations_changed.getRemovedValues().map(ta => ta.initAnimation())
      tour_animations_changed.getAddedValues().map(ta => ta.deleteCurrentLayers())
    }

    if (changes.navigation && changes.navigation.currentValue) {
      const zoom = changes.navigation.currentValue.zoom
      const center = changes.navigation.currentValue.center
      if (center) {
        this.map.flyTo({center: changes.navigation.currentValue.center, duration: 400, zoom})
      } else if (zoom) {
        this.map.setZoom(zoom)
      }
    }
  }

  async waitForMapAndProcessChanges() {
    if (!this.runningChange) {
      this.runningChange = true
      await this.waitForMapStyle()
      while (this.changesQueue.length) {
        await this.processChanges(this.changesQueue.splice(0, 1)[0])
      }
      this.runningChange = false
    }
  }

  async waitForMapStyle() {
    let done = false
    while (!done) {
      await PAUtil.sleep(50)
      try {
        if (this.map) {
          this.map.getStyle()
          done = true
        }
      } catch (err) {
        console.log(err)
        await this.waitForMapStyle()
        done = true
      }
    }
  }

  cloneChanges(changes: SimpleChanges): SimpleChanges {
    // cloning changes to prevent first changes object values being overridden with later changes object values by angular source code for some reason
    return  {
      ...changes,
    }
  }

  private async waitForMapWindow(tries: number) {
    while (!document.getElementById('map_window') && tries > 0) {
      console.log('Trying to get map: attempt ' + tries)
      await PAUtil.sleep(100)
      if (tries) tries -= 1
    }
  }
}


